import React, {useState} from 'react'
import Icon from '../../core/shared/Icon'
import {CartItem, computeCartItemPrice} from './cart-item'
import {useFlags} from 'launchdarkly-react-client-sdk'
import {formatPrice} from '../../utils/currency-format'
import {
  SubAccordion,
  SectionBlock,
  SubAccordionHeader,
  SubAccordionMeta,
  SubAccordionTotal,
  ChevronIcon,
  LocationAccordion,
  LocationAccordionWrapper,
  LocationHeader,
  LocationMeta,
  ItemRow,
  ItemInfo,
  ItemName,
  ItemPriceWrapper,
  ItemPrice,
} from '../../features/OrderSummary/OrderSummary.styles'
import {ONE_TIME_FEES} from './constants'
import {groupItemsByLocation, getLocationLabel} from './utils'

function categorizeTodayItems(items) {
  const software = []
  const addons = []
  const otf = []
  const coupons = []
  items.forEach(item => {
    if (item.isCouponName) return
    if (item.isIncentive) {
      coupons.push(item)
      return
    }
    const nameLower = item?.name?.toLowerCase() ?? ''
    const isOTF = ONE_TIME_FEES.some(fee => nameLower.includes(fee))
    // All plans from the API share prodType: 'plan' (SEO, MC PLUS, ETC). True software centers
    // (CC, MC, WC, RC, Keap Max) have no groupTitle; add-on-like plans
    // (SEO, TL, Strategic Content, Call Tracking) have a groupTitle set by
    // the catalog, so we use that to route them to the correct section.
    if (item.prodType === 'plan' && !item.groupTitle) software.push(item)
    else if (isOTF) otf.push(item)
    else addons.push(item)
  })
  return {software, addons, otf, coupons}
}

function getGroupTotal(items, country, paymentPreview) {
  const total = items.reduce((sum, item) => {
    const itemTotal = computeCartItemPrice(item, {
      isToday: true,
      paymentPreview,
      country,
    })
    const addonTotal = (item.addons || [])
      .filter(a => a.prodType === 'itemBasedAddon' && !a.isExisting)
      .reduce((addonSum, addon) => {
        const previewAddon = paymentPreview?.apps?.find(
          ({addonCode}) => addonCode === addon.code,
        )
        if (previewAddon) {
          const isAusNzAddon = ['AUD', 'NZD'].includes(paymentPreview?.currency)
          return (
            addonSum +
            (isAusNzAddon ? previewAddon.total : previewAddon.subtotal)
          )
        }
        const currencyCode = item.currency?.currency
        const addonCurrency = addon.currencies?.find(
          c => c.currency === currencyCode,
        )
        const addonPrice =
          (addonCurrency?.unitAmount || 0) * (addon.quantity || 1)
        const isSeobaseAddon = [
          'addon-5-seopb-m2m',
          'addon-5-seokey-m2m',
          'addon-4-seopb-m2m',
          'addon-4-seokey-m2m',
        ].includes(addon.code)
        return (
          addonSum +
          (currencyCode === 'AUD' && isSeobaseAddon
            ? addonPrice * 1.1
            : addonPrice)
        )
      }, 0)
    return sum + itemTotal + addonTotal
  }, 0)
  return formatPrice(total, 1, '', country)
}

function getCouponTotal(coupons, groupItems, paymentPreview, country) {
  let total = 0
  if (paymentPreview) {
    total = groupItems
      .filter(
        item => !item.isIncentive && !item.isCouponName && !item.isExisting,
      )
      .reduce((sum, item) => sum + (item.discount || 0), 0)
  } else {
    total = coupons.reduce(
      (sum, item) => sum + Math.abs(item.currency?.unitAmount || 0),
      0,
    )
  }
  return formatPrice(-total, 1, '', country)
}

// One "Software"/"Add-ons"/"One Time Fees"/"Coupons & Discounts" block —
// factored out since it now renders once per location group instead of once
// per page.
function CategoryAccordion({
  label,
  items,
  country,
  open,
  onToggle,
  paymentPreview,
  showTotal = true,
  overrideTotal,
}) {
  if (!items.length) return null
  return (
    <SubAccordion style={{marginBottom: '8px'}}>
      <SubAccordionHeader onClick={onToggle}>
        <SubAccordionMeta>
          <ChevronIcon open={open}>
            <Icon name="downArrow" />
          </ChevronIcon>
          <div className="sub-category">
            {label} {items.length > 0 && <span>- {items.length} items</span>}
          </div>
        </SubAccordionMeta>
        {showTotal && (
          <SubAccordionTotal>
            {overrideTotal !== undefined
              ? overrideTotal
              : getGroupTotal(items, country, paymentPreview)}
          </SubAccordionTotal>
        )}
      </SubAccordionHeader>
      {open && (
        <SectionBlock>
          {items.map((item, index) => (
            <CartItem
              key={index}
              item={item}
              isToday
              paymentPreview={paymentPreview}
              country={country}
            />
          ))}
        </SectionBlock>
      )}
    </SubAccordion>
  )
}

const CCDesktopTodaySummary = ({
  todayItems,
  getProducts,
  isAusNZ,
  hasSupportFee,
  paymentState,
  supportFee,
  country,
}) => {
  // Keyed by `${locationIndex}-${category}` (e.g. "0-software") so each
  // location's four category blocks toggle independently — everything
  // defaults open (`!== false`) until explicitly collapsed, matching the
  // single-location behavior this replaces.
  const [openSections, setOpenSections] = useState({})
  const toggleSection = key =>
    setOpenSections(prev => ({...prev, [key]: prev[key] === false}))
  const {frontendPreviewPayment} = useFlags()

  const paymentPreview = paymentState.paymentInfo?.paymentPreview
  const locationPreviews = paymentState.paymentInfo?.locationPreviews
  const isAus = paymentState.paymentInfo?.country === 'AU'
  const allTodayItems = todayItems
    ? getProducts(todayItems, true).filter(item => {
        if (item.code === 'urldbdesignfee' && item.isCoupon) return false
        return item.code !== '247support-m2m'
      })
    : []
  // Multi-location Parent Pay orders tag every plans[] entry with the
  // location it came from (order_line.location_thryv_id) — group by that so
  // each location gets its own set of category accordions, same as the rep-
  // facing Payment Details page. Single-location orders (or ones predating
  // this column) collapse to exactly one group, so nothing changes for them.
  const locationGroups = groupItemsByLocation(
    allTodayItems,
    paymentState.paymentInfo?.thryvId,
  )
  const showLocationHeaders = locationGroups.length > 1

  function getTaxAmount() {
    if (frontendPreviewPayment) {
      // Multi-location: sum tax across all per-location previews
      if (locationPreviews) {
        const totalTax = Object.values(locationPreviews).reduce(
          (sum, lp) => sum + (lp.tax ?? 0),
          0,
        )
        if (totalTax > 0) return totalTax
      } else if (paymentState.paymentInfo?.paymentPreview?.tax > 0) {
        return paymentState.paymentInfo.paymentPreview.tax
      }
    }
    if (paymentState.calculatedTaxes === null) {
      return 'To be calculated'
    }
    if (!frontendPreviewPayment || !paymentState.paymentInfo?.paymentPreview) {
      return paymentState.calculatedTaxes
    }
    return paymentState.paymentInfo.paymentPreview.tax
  }

  function getFeeAmount() {
    if (!frontendPreviewPayment || !paymentPreview) {
      return parseFloat(supportFee.currency.unitAmount)
    }
    const {subtotal, tax} = paymentPreview.apps.find(
      ({planCode}) => planCode === '247support-m2m',
    )
    return isAus ? Math.max(subtotal + tax, 0) : Math.max(subtotal, 0)
  }

  return (
    <>
      <LocationAccordion>
        {locationGroups.map(group => {
          // For multi-location orders use the per-location preview keyed by
          // locationThryvId; fall back to the order-level paymentPreview for
          // single-location orders or locations with no preview entry.
          const groupPaymentPreview =
            locationPreviews?.[group.locationThryvId] ?? paymentPreview

          const {software, addons, otf, coupons} = categorizeTodayItems(
            group.items,
          )

          // For multi-location orders, getProducts may not generate isIncentive
          // rows (items lack a discount field when paymentPreview is absent).
          // Fall back to deriving the coupon display from locationPreviews data:
          // filter order-level coupons to those applicable to this location's
          // items, then use the location preview's discount as the section total.
          const locationDiscount = groupPaymentPreview?.discount ?? 0
          const effectiveCoupons = (() => {
            if (coupons.length > 0) return coupons
            if (locationDiscount <= 0) return coupons
            const orderCoupons = paymentState.paymentInfo?.coupons ?? []
            const groupItemCodes = new Set(
              group.items
                .filter(i => !i.isIncentive && !i.isCouponName)
                .map(i => i.code),
            )
            const seen = new Set()
            const applicable = orderCoupons.filter(c => {
              if (!c.code || seen.has(c.code)) return false
              // When appliesToItems is populated, it is the authoritative list —
              // check intersection with this location's items regardless of
              // appliesToAllPlans (which in Recurly means "plan-type charges",
              // not "every plan in the order").
              const targets = c.appliesToItems ?? []
              if (targets.length > 0) {
                if (targets.some(code => groupItemCodes.has(code))) {
                  seen.add(c.code)
                  return true
                }
                return false
              }
              // No item restriction — include for any location with a discount.
              seen.add(c.code)
              return true
            })
            if (applicable.length === 0) return coupons
            return applicable.map(c => ({
              name: c.name || c.description || c.code,
              isIncentive: true,
              currency: {unitAmount: 0},
            }))
          })()
          const couponTotal =
            coupons.length === 0 && locationDiscount > 0
              ? formatPrice(-locationDiscount, 1, '', country)
              : getCouponTotal(
                  coupons,
                  group.items,
                  groupPaymentPreview,
                  country,
                )

          const nonCouponItems = group.items.filter(
            item => !item.isIncentive && !item.isCouponName,
          )
          const groupTotal = getGroupTotal(
            nonCouponItems,
            country,
            groupPaymentPreview,
          )
          const categoryProps = category => ({
            country,
            paymentPreview: groupPaymentPreview,
            open: openSections[`${group.index}-${category}`] !== false,
            onToggle: () => toggleSection(`${group.index}-${category}`),
          })
          const categories = (
            <>
              <CategoryAccordion
                label="Software"
                items={software}
                {...categoryProps('software')}
              />
              <CategoryAccordion
                label="Add-ons"
                items={addons}
                {...categoryProps('addons')}
              />
              <CategoryAccordion
                label="One Time Fees"
                items={otf}
                {...categoryProps('otf')}
              />
              <CategoryAccordion
                label="Coupons & Discounts"
                items={effectiveCoupons}
                overrideTotal={couponTotal}
                {...categoryProps('coupons')}
              />
            </>
          )

          if (!showLocationHeaders) return categories

          const locationOpen = openSections[`loc-${group.index}`] !== false
          return (
            <LocationAccordionWrapper key={group.locationThryvId ?? 'none'}>
              <LocationHeader
                onClick={() => toggleSection(`loc-${group.index}`)}
              >
                <LocationMeta>
                  <span className="pin">
                    <Icon name="loc" size={12} />
                  </span>
                  <span className="loc-name">
                    {getLocationLabel(
                      group,
                      paymentState.paymentInfo?.locationNames,
                    )}
                  </span>
                  <span className="loc-count">
                    {' '}
                    - {group.items.length} items
                  </span>
                </LocationMeta>
                <span className="loc-total">
                  {groupTotal}
                  <ChevronIcon open={locationOpen}>
                    <Icon name="downArrow" size="xs" />
                  </ChevronIcon>
                </span>
              </LocationHeader>
              {locationOpen && categories}
            </LocationAccordionWrapper>
          )
        })}
      </LocationAccordion>

      {/* Tax and the 24/7 Service & Support Fee are order-level totals, not
          tied to any one location's line items (a single combined amount
          for the whole order, computed server-side), so they render once
          here rather than inside any location's accordion. */}
      {(!isAusNZ || hasSupportFee) && (
        <SectionBlock style={{marginBottom: '8px'}}>
          {!isAusNZ && (
            <ItemRow selected>
              <ItemInfo>
                <ItemName selected>Tax</ItemName>
              </ItemInfo>
              <ItemPriceWrapper>
                <ItemPrice>
                  {formatPrice(getTaxAmount(), 1, '', country)}
                </ItemPrice>
              </ItemPriceWrapper>
            </ItemRow>
          )}
          {hasSupportFee && (
            <div className="d-flex justify-content-between">
              <p className="paragraph-cart-item">
                24/7 Service &amp; Support Fee
              </p>
              <p className="paragraph-cart-item">
                {formatPrice(getFeeAmount(), 1, '', country)}
              </p>
            </div>
          )}
        </SectionBlock>
      )}

      {Boolean(
        paymentState?.paymentInfo?.balanceInfo?.availableCreditAmount,
      ) && (
        <SectionBlock style={{marginBottom: '8px'}}>
          <ItemRow selected>
            <ItemInfo>
              <ItemName selected>Credit</ItemName>
            </ItemInfo>
            <ItemPriceWrapper>
              <ItemPrice>
                {formatPrice(
                  paymentState?.paymentInfo?.balanceInfo
                    ?.availableCreditAmount || 0,
                  1,
                  '',
                  country,
                )}
              </ItemPrice>
            </ItemPriceWrapper>
          </ItemRow>
        </SectionBlock>
      )}

      {isAus && (
        <p
          style={{
            fontSize: 'clamp(11px, 1.2vw, 13px)',
            color: '#4D4D4D',
            marginTop: '10px',
            marginBottom: 0,
          }}
        >
          {'*All plans include GST'}
        </p>
      )}
    </>
  )
}

export {CCDesktopTodaySummary}
